import * as Yup from 'yup'
import { Col, Form, InputGroup, Row } from 'react-bootstrap'
import { getColumnValues } from '../../../common'

type DesignedHeading = {
  numberOfOccupyingColumns?: number
  numberOfOccupyingRows?: number
}

type FigureValues = {
  numberOfOccupyingColumns?: number
}

type Values = {
  id: number
  useAuxiliaryLine: boolean
  horizontalDesignedHeading?: DesignedHeading
  verticalDesignedHeading?: DesignedHeading
  headingNumberOfOccupyingColumns: number
  preambleHeading?: { numberOfOccupyingColumns?: number }
  preambleNumberOfColumns?: number
  figures: Record<string, FigureValues>
}

type NestedErrors = {
  numberOfOccupyingColumns?: string
  numberOfOccupyingRows?: string
}

type Errors = {
  useAuxiliaryLine?: string
  horizontalDesignedHeading?: NestedErrors
  verticalDesignedHeading?: NestedErrors
  headingNumberOfOccupyingColumns?: string
  preambleHeading?: { numberOfOccupyingColumns?: string }
  preambleNumberOfColumns?: string
  figures?: Record<string, { numberOfOccupyingColumns?: string }>
}

type Touched = {
  useAuxiliaryLine?: boolean
  horizontalDesignedHeading?: { numberOfOccupyingColumns?: boolean; numberOfOccupyingRows?: boolean }
  verticalDesignedHeading?: { numberOfOccupyingColumns?: boolean; numberOfOccupyingRows?: boolean }
  headingNumberOfOccupyingColumns?: boolean
  preambleHeading?: { numberOfOccupyingColumns?: boolean }
  preambleNumberOfColumns?: boolean
  figures?: Record<string, { numberOfOccupyingColumns?: boolean }>
}

type EnumValues = {
  figureOrientations: Record<string, string>
}

type Rule = {
  preambleNumberOfColumns?: number
  figures: Record<string, unknown>
}

type Props = {
  values: Values | null
  errors: Errors
  touched: Touched
  handleChange: React.ChangeEventHandler<HTMLElement>
  handleBlur: React.FocusEventHandler<HTMLElement>
  handleSubmit: (values: Values) => void
  enumValues: EnumValues
  isSettingWritable: boolean
  showExperimentalFeatures: boolean
  rule: Rule
  ruleIdx: number
  listTitle: string
  patternRulesLength: number
  className: string
}

const _ArticleRuleForm = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  handleSubmit,
  enumValues,
  isSettingWritable,
  showExperimentalFeatures,
  rule,
  ruleIdx,
  listTitle,
  patternRulesLength,
  className
}: Props) => {
  if (!values) {
    return <></>
  }
  const DesignedHeadingControl = () => {
    if (!values.horizontalDesignedHeading && !values.verticalDesignedHeading) {
      return <>なし</>
    }
    const orientation = values.verticalDesignedHeading !== undefined ? 'vertical' : 'horizontal'
    const designedHeadingKey =
      values.verticalDesignedHeading !== undefined ? 'verticalDesignedHeading' : 'horizontalDesignedHeading'
    return (
      <InputGroup size='sm' hasValidation>
        <InputGroup.Text>{enumValues.figureOrientations[orientation]}</InputGroup.Text>
        <Form.Control
          id={`${designedHeadingKey}.numberOfOccupyingColumns-${values.id}`}
          type='number'
          name={`${designedHeadingKey}.numberOfOccupyingColumns`}
          value={values?.[designedHeadingKey]?.numberOfOccupyingColumns}
          disabled={!isSettingWritable}
          onChange={handleChange}
          onBlur={(_) => handleSubmit(values)}
          isInvalid={
            !!(
              touched?.[designedHeadingKey]?.numberOfOccupyingColumns &&
              errors?.[designedHeadingKey]?.numberOfOccupyingColumns
            )
          }
        />
        <Form.Control.Feedback type='invalid'>
          {errors?.[designedHeadingKey]?.numberOfOccupyingColumns}
        </Form.Control.Feedback>
        <InputGroup.Text>段</InputGroup.Text>
        <Form.Control
          id={`${designedHeadingKey}.numberOfOccupyingRows-${values.id}`}
          type='number'
          name={`${designedHeadingKey}.numberOfOccupyingRows`}
          value={values?.[designedHeadingKey]?.numberOfOccupyingRows}
          disabled={!isSettingWritable}
          onChange={handleChange}
          onBlur={(_) => handleSubmit(values)}
          isInvalid={
            !!(
              touched?.[designedHeadingKey]?.numberOfOccupyingRows &&
              errors?.[designedHeadingKey]?.numberOfOccupyingRows
            )
          }
        />
        <Form.Control.Feedback type='invalid'>
          {errors?.[designedHeadingKey]?.numberOfOccupyingRows}
        </Form.Control.Feedback>
        <InputGroup.Text>行</InputGroup.Text>
      </InputGroup>
    )
  }
  const columnValues = getColumnValues(showExperimentalFeatures)

  return (
    <Row
      key={ruleIdx}
      className={`p-2 ${ruleIdx < patternRulesLength - 1 ? 'border-bottom' : ''} article-rule-${className}`}
    >
      <Col sm={3} md={2}>
        {listTitle}
      </Col>
      <Col sm={9} md={10}>
        <Row>
          <Col sm={3} md={2}>
            補助線
          </Col>
          <Col>
            <Form.Check
              type='switch'
              id='useAuxiliaryLineSwitch'
              label='補助線あり'
              name='useAuxiliaryLine'
              value={String(values.useAuxiliaryLine)}
              checked={values.useAuxiliaryLine}
              onChange={(e) => {
                handleChange(e)
                handleSubmit(values)
              }}
              onBlur={handleBlur}
              isInvalid={!!(touched.useAuxiliaryLine && errors.useAuxiliaryLine)}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={3} md={2}>
            見出し
          </Col>
          <Col sm={9} md={10}>
            <Row>
              <Col sm={6} md={4}>
                カット見出し
              </Col>
              <Col sm={3} md={4}>
                <DesignedHeadingControl />
              </Col>
            </Row>
            <Row>
              <Col sm={6} md={4}>
                通常見出し
              </Col>
              <Col sm={3} md={4}>
                <InputGroup size='sm' hasValidation>
                  <Form.Control
                    id={`headingNumberOfOccupyingColumns-${values.id}`}
                    type='number'
                    name='headingNumberOfOccupyingColumns'
                    value={values.headingNumberOfOccupyingColumns}
                    disabled={!isSettingWritable}
                    onChange={handleChange}
                    onBlur={(_) => handleSubmit(values)}
                    isInvalid={!!(touched.headingNumberOfOccupyingColumns && errors.headingNumberOfOccupyingColumns)}
                  />
                  <InputGroup.Text>段</InputGroup.Text>
                  <Form.Control.Feedback type='invalid'>{errors.headingNumberOfOccupyingColumns}</Form.Control.Feedback>
                </InputGroup>
              </Col>
            </Row>
            {values.preambleHeading && (
              <Row>
                <Col sm={6} md={4}>
                  前文横見出し
                </Col>
                <Col sm={3} md={4}>
                  <InputGroup size='sm' hasValidation>
                    <Form.Control
                      id={`preambleHeading.numberOfOccupyingColumns-${values.id}`}
                      type='number'
                      name='preambleHeading.numberOfOccupyingColumns'
                      value={values.preambleHeading?.numberOfOccupyingColumns}
                      disabled={!isSettingWritable}
                      onChange={handleChange}
                      onBlur={(_) => handleSubmit(values)}
                      isInvalid={
                        !!(
                          touched.preambleHeading?.numberOfOccupyingColumns &&
                          errors.preambleHeading?.numberOfOccupyingColumns
                        )
                      }
                    />
                    <InputGroup.Text>段</InputGroup.Text>
                    <Form.Control.Feedback type='invalid'>
                      {errors.preambleHeading?.numberOfOccupyingColumns}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
        {rule.preambleNumberOfColumns && (
          <Row>
            <Col sm={3} md={2}>
              前文
            </Col>
            <Col sm={3} md={3}>
              <InputGroup size='sm' hasValidation>
                <Form.Control
                  id={`preambleNumberOfColumns-${values.id}`}
                  type='number'
                  name='preambleNumberOfColumns'
                  value={values.preambleNumberOfColumns}
                  disabled={!isSettingWritable}
                  onChange={handleChange}
                  onBlur={(_) => handleSubmit(values)}
                  isInvalid={!!(touched.preambleNumberOfColumns && errors.preambleNumberOfColumns)}
                />
                <InputGroup.Text>段</InputGroup.Text>
                <Form.Control.Feedback type='invalid'>{errors.preambleNumberOfColumns}</Form.Control.Feedback>
              </InputGroup>
            </Col>
          </Row>
        )}
        <Row>
          <Col sm={3} md={2}>
            写真
          </Col>
          <Col sm={3} md={3}>
            {Object.keys(rule.figures).map((orientation) => (
              <Row key={orientation}>
                <Col>
                  <InputGroup size='sm' hasValidation>
                    <InputGroup.Text>{enumValues.figureOrientations[orientation]}</InputGroup.Text>
                    <Form.Control
                      type='number'
                      as='select'
                      name={`figures.${orientation}.numberOfOccupyingColumns`}
                      value={values.figures?.[orientation]?.numberOfOccupyingColumns || ''}
                      onChange={handleChange}
                      onBlur={(_) => handleSubmit(values)}
                      isInvalid={
                        !!(
                          touched.figures?.[orientation]?.numberOfOccupyingColumns &&
                          errors.figures?.[orientation]?.numberOfOccupyingColumns
                        )
                      }
                      disabled={!isSettingWritable}
                    >
                      {columnValues.map(({ label, value }, idx) => (
                        <option key={idx} value={value}>
                          {label}
                        </option>
                      ))}
                    </Form.Control>
                    <Form.Control.Feedback type='invalid'>
                      {errors.figures?.[orientation]?.numberOfOccupyingColumns}
                    </Form.Control.Feedback>
                  </InputGroup>
                </Col>
              </Row>
            ))}
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export const ArticleRuleForm = Object.assign(_ArticleRuleForm, {
  schema: Yup.object().shape({
    headingNumberOfOccupyingColumns: Yup.number().min(1).required(),
    preambleNumberOfColumns: Yup.number().min(1),
    preambleHeading: Yup.object().shape({
      numberOfOccupyingColumns: Yup.number().min(1)
    }),
    horizontalDesignedHeading: Yup.object().shape({
      numberOfOccupyingRows: Yup.number().min(1),
      numberOfOccupyingColumns: Yup.number().min(1)
    }),
    verticalDesignedHeading: Yup.object().shape({
      numberOfOccupyingRows: Yup.number().min(1),
      numberOfOccupyingColumns: Yup.number().min(1)
    }),
    figures: Yup.object().shape({
      vertical: Yup.object().shape({
        numberOfOccupyingColumns: Yup.number().min(0.5).required()
      }),
      horizontal: Yup.object().shape({
        numberOfOccupyingColumns: Yup.number().min(0.5).required()
      })
    })
  })
})
